// ./RegistrationFields/CompanyManagementType/CompanyManagementType.jsx

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useIntl } from '@edx/frontend-platform/i18n';
import {
  FormAutosuggest,
  FormAutosuggestOption,
  FormControlFeedback,
} from '@openedx/paragon';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { clearRegistrationBackendError } from '../../data/actions';
import messages from '../../messages';

const GENDER_VALUE_KEY = 'value';
const GENDER_LABEL_KEY = 'label';

const DEFAULT_GENDER_TYPE_LIST = [
  { value: 'm', label: 'Male' },
  { value: 'f', label: 'Female' },
  { value: 'o', label: 'Other' },
];

const GenderType = (props) => {
  const {
    name,
    value,
    handleChange,
    handleErrorChange,
    errorMessage,
    floatingLabel,
    genderTypeList,
  } = props;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const genderTypes = genderTypeList || DEFAULT_GENDER_TYPE_LIST;
  const [inputText, setInputText] = useState('');

  const selected = genderTypes.find(
    item => item[GENDER_VALUE_KEY] === value,
  ) || { value: '', label: '' };

  useEffect(() => {
    if (value) {
      setInputText(selected[GENDER_LABEL_KEY] || '');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldValue = {
    userProvidedText: inputText,
    selectionValue: selected[GENDER_LABEL_KEY] || '',
    selectionId: selected[GENDER_VALUE_KEY] || '',
  };

  const runValidation = () => {
    handleErrorChange(name, '');
  };

  const handleOnFocus = () => {
    dispatch(clearRegistrationBackendError(name));
    handleErrorChange(name, '');
  };

  const handleOnBlur = () => {
    runValidation();
  };

  const handleOnChangeAutosuggest = (autosuggestValue) => {
    const code = autosuggestValue.selectionId || '';
    setInputText(autosuggestValue.userProvidedText || autosuggestValue.selectionValue || '');

    handleChange({
      target: {
        name,
        value: code,
      },
    });

    runValidation();
  };

  const getOptions = () => genderTypes.map(option => (
    <FormAutosuggestOption
      key={option[GENDER_VALUE_KEY]}
      id={option[GENDER_VALUE_KEY]}
    >
      {option[GENDER_LABEL_KEY]}
    </FormAutosuggestOption>
  ));
  const hasMatchingOptions = !inputText.trim()
    || genderTypes.some(option => option[GENDER_LABEL_KEY].toLowerCase().includes(inputText.toLowerCase()));

  return (
    <div className="mb-4">
      <FormAutosuggest
        floatingLabel={
          floatingLabel
          || formatMessage(messages['registration.gender.label'])
        }
        aria-label="gender type"
        name={name}
        value={fieldValue || {}}
        className={classNames({ 'form-field-error': errorMessage })}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onChange={handleOnChangeAutosuggest}
      >
        {getOptions()}
      </FormAutosuggest>
      {errorMessage && (
        <FormControlFeedback
          key="error"
          className="form-text-size"
          hasIcon={false}
          feedback-for={name}
          type="invalid"
        >
          {errorMessage}
        </FormControlFeedback>
      )}
      {!errorMessage && !hasMatchingOptions && (
        <div className="autosuggest-no-results">{formatMessage(messages['registration.no.results.found'])}</div>
      )}
    </div>
  );
};

GenderType.propTypes = {
  name: PropTypes.string.isRequired,          // e.g. "genderType"
  value: PropTypes.string.isRequired,         // e.g. "Male"
  handleChange: PropTypes.func.isRequired,
  handleErrorChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  floatingLabel: PropTypes.string,
  genderTypeList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
};

GenderType.defaultProps = {
  errorMessage: '',
  floatingLabel: '',
  genderTypeList: null,
};

export default GenderType;
